/* NOTE: This is exporting a Singleton instance of this class (see bottom of the file); */

class AdminApiSdk {
    #_customHeaderFn = async function () { };
    #_baseBiddingUri;
    /*****************************************************/
    /* START Public Methods */
    /*****************************************************/
    async sendRequest(url, method, body) {
        const customHeaders = await this.#_customHeaderFn({ method, url });
        const hasBody = body !== undefined && method !== 'GET' && method !== 'HEAD';
        const options = {
            method,
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer",
        };
        if (hasBody) {
            options.headers["Content-Type"] = "application/json";
            options.body = JSON.stringify(body);
        }
        const response = await fetch(url, options);
        if (!response.ok) {
            const err = new Error(`Received response status code: ${response.status}`);
            err.statusCode = response.status;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
                const responseBody = await response.json();
                err.body = responseBody;
            }
            throw err;
        } else {
            const contentType = response.headers.get('content-type');
            const contentLength = response.headers.get('content-length');
            let responseData;
            if (contentType && contentType.indexOf('application/json') !== -1) {
                responseData = await response.json();
            } else if (contentLength > 0) {
                responseData = await response.text();
            }
            return responseData;
        }
    }
    // --START EVENT & ITEM DYNAMIC RULE CHANGES --//
    async eventChangeAskingPrice(eventId, askingPrice) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/eventChangeAskingPrice`;
        await this.sendRequest(url, 'PUT', { askingPrice })
    }
    async itemChangeAskingPrice(eventId, itemId, askingPrice) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/itemChangeAskingPrice`;
        await this.sendRequest(url, 'PUT', { itemId, askingPrice })
    }
    async eventChangeBidIncrement(eventId, increment) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/eventChangeBidIncrement`;
        await this.sendRequest(url, 'PUT', { increment })
    }
    async itemChangeBidIncrement(eventId, itemId, increment) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/itemChangeBidIncrement`;
        await this.sendRequest(url, 'PUT', { itemId, increment })
    }
    async eventChangeBuyNowAmount(eventId, buyNowAmount) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/eventChangeBuyNowAmount`;
        await this.sendRequest(url, 'PUT', { buyNowAmount })
    }
    async itemChangeBuyNowAmount(eventId, itemId, buyNowAmount) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/itemChangeBuyNowAmount`;
        await this.sendRequest(url, 'PUT', { itemId, buyNowAmount })
    }
    async eventChangeBuyNowDisableThreshold(eventId, buyNowDisableThreshold) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/eventChangeBuyNowDisableThreshold`;
        await this.sendRequest(url, 'PUT', { buyNowDisableThreshold })
    }
    async itemChangeBuyNowDisableThreshold(eventId, itemId, buyNowDisableThreshold) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/itemChangeBuyNowDisableThreshold`;
        await this.sendRequest(url, 'PUT', { itemId, buyNowDisableThreshold })
    }
    async eventChangeOfferPrice(eventId, offerPrice) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/eventChangeOfferPrice`;
        await this.sendRequest(url, 'PUT', { offerPrice })
    }
    async itemChangeOfferPrice(eventId, itemId, offerPrice) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/itemChangeOfferPrice`;
        await this.sendRequest(url, 'PUT', { itemId, offerPrice })
    }
    async itemChangeReservePrice(eventId, itemId, reservePrice) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/itemChangeReservePrice`;
        await this.sendRequest(url, 'PUT', { itemId, reservePrice })
    }
    async stopEvent(eventId, message) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/stopEvent`;
        await this.sendRequest(url, 'PUT', { message })
    }
    async pauseEvent(eventId, reason) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/pauseEvent`;
        await this.sendRequest(url, 'PUT', { reason })
    }
    async resumeEvent(eventId) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/resumeEvent`;
        await this.sendRequest(url, 'PUT')
    }
    async stopItem(eventId, itemId, message) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/stopItem`;
        await this.sendRequest(url, 'PUT', { itemId, message })
    }
    async restartItem(eventId, itemId) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/restartItemBidding`;
        await this.sendRequest(url, 'PUT', { itemId })
    }
    // --END EVENT & ITEM DYNAMIC RULE CHANGE CALLS --//
    // --START EVENT & ITEM REST CALLS --//
    async getEventActivities(includeProcessedEntriesFlag = false) {
        const procEntryExclude = `projection-type=exclude&projection-exclude-properties=processedEntries`;
        let url = `${this.#_baseBiddingUri}/event-activitys`;
        if (!includeProcessedEntriesFlag) {
            url += `?${procEntryExclude}`;
        }
        const response = await this.sendRequest(url, 'GET');
        return response;
    }
    async getItemBiddings(includeProcessedEntriesFlag = false) {
        const procEntryExclude = `projection-type=exclude&projection-exclude-properties=processedEntries`;
        let url = `${this.#_baseBiddingUri}/item-biddings`;
        if (!includeProcessedEntriesFlag) {
            url += `?${procEntryExclude}`;
        }
        const response = await this.sendRequest(url, 'GET');
        return response;
    }
    async getEventEngineStates() {
        const url = `${this.#_baseBiddingUri}/event-engine-states`;
        const response = await this.sendRequest(url, 'GET');
        return response;
    }
    async getItemEngineStates() {
        const url = `${this.#_baseBiddingUri}/item-engine-states`;
        const response = await this.sendRequest(url, 'GET');
        return response;
    }
    async getEngineSystemStatus() {
        const url = `${this.#_baseBiddingUri}/event-runtime-engine-system-status`;
        const response = await this.sendRequest(url, 'GET');
        return response;
    }
    async getItemStatistics() {
        const url = `${this.#_baseBiddingUri}/event-runtime-engine/item-statistics`;
        const response = await this.sendRequest(url, 'GET');
        return response;
    }
    async getEventStatistics() {
        const url = `${this.#_baseBiddingUri}/event-runtime-engine/event-statistics`;
        const response = await this.sendRequest(url, 'GET');
        return response;
    }

    // --END EVENT & ITEM REST CALLS --//
    // --START SIMULCAST CLERK-SPECIFIC CALLS --//
    async clerkProposeBid(itemId, amount) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/clerkProposeBid`;
        await this.sendRequest(url, 'PUT', { amount })
    }
    async clerkSetBidIncrement(itemId, increment) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/clerkSetBidIncrement`;
        await this.sendRequest(url, 'PUT', { increment })
    }
    async clerkSetAskingPrice(itemId, askingPrice) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/clerkSetAskingPrice`;
        await this.sendRequest(url, 'PUT', { askingPrice })
    }
    async moveToItem(eventId, itemId) {
        const url = `${this.#_baseBiddingUri}/event-raw-activity/${eventId}/actions/clerk/moveToItem`;
        await this.sendRequest(url, 'PUT', { itemId })
    }
    async finalizeSale(itemId, finalState, bid, badge) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/finalizeSale`;
        await this.sendRequest(url, 'PUT', { finalState, badge, bid });
    }
    async setWinningFloorBadge(itemId, badge) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/setFloorBadge`;
        await this.sendRequest(url, 'PUT', { badge });
    }
    async pauseSale(itemId, reason) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/pauseSale`;
        await this.sendRequest(url, 'PUT', { reason });
    }
    async resumeSale(itemId) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/resumeSale`;
        await this.sendRequest(url, 'PUT');
    }
    async clerkDeleteProxyBids(itemId) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/deleteProxyBids`;
        await this.sendRequest(url, 'PUT');
    }
    async clerkResetBidding(itemId) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/resetBidding`;
        await this.sendRequest(url, 'PUT');
    }
    async clerkFloorOverride(itemId) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/floorOverride`;
        await this.sendRequest(url, 'PUT');
    }
    async clerkUndo(itemId) {
        const url = `${this.#_baseBiddingUri}/item-raw-bidding/${itemId}/actions/clerk/undo`;
        await this.sendRequest(url, 'PUT');
    }
    // --END SIMULCAST CLERK-SPECIFIC CALLS --//
    // --START HDT-SPECIFIC CALLS --//
    async getItemBiddingById(itemId) {
        const url = `${this.#_baseBiddingUri}/item-bidding/${itemId}`;
        const customHeaders = await this.#_customHeaderFn({ method: 'GET', url });
        const response = await fetch(url, {
            method: 'GET',
            mode: "cors",
            cache: "no-cache",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json",
                ...customHeaders
            },
            redirect: "follow",
            referrerPolicy: "no-referrer"
        });
        if (!response.ok) {
            const err = new Error('Error getting ItemBidding by id.');
            err.statusCode = response.status;
            if (response.headers.get('Content-Length') !== '0') {
                const responseBody = await response.json();
                err.body = responseBody;
            }
            throw err;
        } else {
            const data = await response.json();
            if (data.retrieved?.proxyBids && data.retrieved?.processedEntries) {
                return data;
            } else {
                throw new Error(`NOT_AUTHORIZED to access item (${itemId}`)
            }
        }
    }
    // --END HDT-SPECIFIC CALLS --//
    /*****************************************************/
    /* END Public Methods */
    /* START Public Accessors */
    /*****************************************************/
    get customHeaderFn() {
        return this.#_customHeaderFn;
    }
    set customHeaderFn(newCustomHeaderFn) {
        this.#_customHeaderFn = newCustomHeaderFn;
    }
    get baseBiddingUri() {
        return this.#_baseBiddingUri;
    }
    set baseBiddingUri(newBaseBiddingUri) {
        this.#_baseBiddingUri = newBaseBiddingUri;
    }
    /*****************************************************/
    /* END Public Accessors */
    /*****************************************************/
}
//SINGLETON Instance Pattern (ESM modules in browser)
const adminEventRuntimeApiSdk = new AdminApiSdk();
export { adminEventRuntimeApiSdk };